import React, { useState } from "react";
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid, 
  ReferenceLine,
  Cell
} from "recharts";
import { 
  FiCheckCircle, 
  FiBarChart2, 
  FiArrowRight, 
  FiFilter, 
  FiDownload,
  FiPieChart
} from "react-icons/fi";
import type { 
  ParetoAnalysis, 
  ParetoDataPoint, 
  RcaMethodCode 
} from "../../../../types/rca.types.js";

export type ParetoWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  readOnly?: boolean;
  onSave?: (analysis: ParetoAnalysis) => void;
  onChainMethod?: (method: RcaMethodCode, defectCategory: string) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const INITIAL_DATA_POINTS: ParetoDataPoint[] = [
  { id: "p-1", defectType: "Scratch", count: 480, percentage: 35.40, cumulativePercentage: 35.40, isVitalFew: true, chainedMethod: "FISHBONE" },
  { id: "p-2", defectType: "Dimension", count: 350, percentage: 25.80, cumulativePercentage: 61.20, isVitalFew: true, chainedMethod: "FIVE_WHY" },
  { id: "p-3", defectType: "Color", count: 200, percentage: 14.80, cumulativePercentage: 76.00, isVitalFew: false },
  { id: "p-4", defectType: "Packing", count: 150, percentage: 11.10, cumulativePercentage: 87.10, isVitalFew: false },
  { id: "p-5", defectType: "Burr", count: 100, percentage: 7.40, cumulativePercentage: 94.50, isVitalFew: false },
  { id: "p-6", defectType: "Others", count: 75, percentage: 5.50, cumulativePercentage: 100.00, isVitalFew: false },
];

export function ParetoWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onSave,
  onChainMethod,
  onRootCauseIdentified,
}: ParetoWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"chart" | "table" | "drilldown">("chart");
  const [dataPoints, setDataPoints] = useState<ParetoDataPoint[]>(INITIAL_DATA_POINTS);
  const [selectedDefect, setSelectedDefect] = useState<string>("Dimension");

  const totalCount = dataPoints.reduce((sum, d) => sum + d.count, 0);
  const vitalFew = dataPoints.filter(d => d.isVitalFew);

  const handleSelectVitalFewDrilldown = (defect: string) => {
    setSelectedDefect(defect);
    onRootCauseIdentified?.(`Priority Defect: ${defect} represents ${dataPoints.find(d => d.defectType === defect)?.percentage}% of total failures.`, "MACHINE", 85);
    onChainMethod?.("FIVE_WHY", defect);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            80/20
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              14. Pareto Analysis Workspace (80/20 Rule)
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                Vital Few Prioritization
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Identify the 20% of defect causes responsible for 80% of problems to prioritize root cause investigation.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("chart")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "chart" 
                  ? "bg-white text-blue-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Chart View
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("table")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "table" 
                  ? "bg-white text-blue-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Data Table
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("drilldown")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "drilldown" 
                  ? "bg-white text-blue-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Drilldowns & Chaining
            </button>
          </div>
        </div>
      </div>

      {/* Vital Few Banner */}
      <div className="px-6 py-2.5 bg-blue-50/50 border-b border-blue-100 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-blue-900">Vital Few Identified (80% Cutoff):</span>
          <div className="flex items-center gap-2">
            {vitalFew.map(v => (
              <span key={v.id} className="px-2.5 py-0.5 rounded-full bg-blue-600 text-white font-bold text-[11px] shadow-sm">
                {v.defectType} ({v.percentage}%)
              </span>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-500 font-medium">
          Total Sample: <span className="font-bold text-slate-900">{totalCount} incidents</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === "chart" ? (
          <div className="space-y-4">
            <div className="h-72 w-full bg-slate-50/70 p-4 rounded-xl border border-slate-200">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dataPoints} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="defectType" stroke="#64748b" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#3b82f6" fontSize={11} label={{ value: "Complaint Count", angle: -90, position: "insideLeft", fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} stroke="#f59e0b" fontSize={11} label={{ value: "Cumulative %", angle: 90, position: "insideRight", fontSize: 10 }} />
                  <Tooltip />
                  <ReferenceLine yAxisId="right" y={80} stroke="#ef4444" strokeDasharray="4 4" label={{ value: "80% Threshold", fill: "#ef4444", fontSize: 10, position: "top" }} />
                  <Bar yAxisId="left" dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                    {dataPoints.map((entry) => (
                      <Cell key={`cell-${entry.id}`} fill={entry.isVitalFew ? "#2563eb" : "#94a3b8"} />
                    ))}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cumulativePercentage" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4, fill: "#f59e0b" }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Quick Chaining Action */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <h4 className="text-xs font-bold text-slate-900">Recommended Next Step:</h4>
                <p className="text-xs text-slate-500">Perform 5-Why or Fishbone root cause analysis on the top vital-few defect: <span className="font-semibold text-blue-600">Dimension (350 count, 61.2% cum.)</span></p>
              </div>
              <button
                type="button"
                onClick={() => handleSelectVitalFewDrilldown("Dimension")}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
              >
                Chain 5-Why on Dimension <FiArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          /* Table Tab */
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Defect Type</th>
                  <th className="py-2.5 px-3">Count</th>
                  <th className="py-2.5 px-3">Percentage (%)</th>
                  <th className="py-2.5 px-3">Cumulative %</th>
                  <th className="py-2.5 px-3">Vital Few?</th>
                  <th className="py-2.5 px-3">Chain RCA Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dataPoints.map(d => (
                  <tr key={d.id} className={d.isVitalFew ? "bg-blue-50/40 font-medium" : ""}>
                    <td className="py-2.5 px-3 font-bold text-slate-900">{d.defectType}</td>
                    <td className="py-2.5 px-3">{d.count}</td>
                    <td className="py-2.5 px-3">{d.percentage}%</td>
                    <td className="py-2.5 px-3 font-mono font-bold text-blue-700">{d.cumulativePercentage}%</td>
                    <td className="py-2.5 px-3">
                      {d.isVitalFew ? (
                        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
                          Vital Few
                        </span>
                      ) : "Useful Many"}
                    </td>
                    <td className="py-2.5 px-3">
                      {d.isVitalFew ? (
                        <button
                          type="button"
                          onClick={() => handleSelectVitalFewDrilldown(d.defectType)}
                          className="px-2 py-1 text-[11px] font-semibold text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          Launch 5-Why →
                        </button>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
